import { useEffect, useState } from "react";
import { subscribeOrders, setOrderStatus, deleteOrder } from "../../../services/orders.service";
import { FiUser, FiPhone, FiMapPin, FiCalendar, FiCheck, FiPackage, FiTrash2 } from "react-icons/fi";

const STATUS_LABELS = {
  pending: { label: "Në pritje", className: "bg-amber-100 text-amber-800" },
  confirmed: { label: "E konfirmuar", className: "bg-green-100 text-green-800" },
  perfunduar: { label: "Perfunduar", className: "bg-[#a6a6a6]/20 text-[#000000]" },
};

const formatDate = (timestamp) => {
  if (!timestamp?.toMillis) return "—";
  const d = new Date(timestamp.toMillis());
  return d.toLocaleDateString("sq-AL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = subscribeOrders((data) => {
      setOrders(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleStatusChange = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    setError("");
    try {
      await setOrderStatus(orderId, newStatus);
    } catch (err) {
      setError(err?.message || "S’u arrit të përditësohet.");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("A jeni të sigurt që dëshironi ta fshini këtë porosi plotësisht?")) return;
    setDeletingId(orderId);
    setError("");
    try {
      await deleteOrder(orderId);
    } catch (err) {
      setError(err?.message || "S’u arrit të fshihet porosia.");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div>
        <h2 className="mb-4 text-2xl font-bold text-[#000000]">
          Porositë
        </h2>
        <p className="text-[#a6a6a6]">Duke ngarkuar porositë...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-1 text-2xl font-bold text-[#000000]">
        Porositë
      </h2>
      <p className="mb-6 text-sm text-[#a6a6a6]">
        Thirrni klientin për të konfirmuar porosinë, pastaj shtypni &quot;E konfirmuar&quot;.
      </p>

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {orders.length === 0 ? (
        <div className="rounded-2xl border border-[#a6a6a6]/30 bg-white p-10 text-center text-[#a6a6a6]">
          Nuk ka porosi ende.
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((order) => {
            const c = order.customer || {};
            const fullName = [c.firstName, c.lastName].filter(Boolean).join(" ") || "—";
            const status = order.status || "pending";
            const statusInfo = STATUS_LABELS[status] || STATUS_LABELS.pending;
            const items = order.items || [];
            const isUpdating = updatingId === order.id;

            return (
              <div
                key={order.id}
                className="rounded-2xl border border-[#a6a6a6]/30 bg-white shadow-sm overflow-hidden"
              >
                {/* Header: date + status + action */}
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#a6a6a6]/30 bg-[#e0d8c3]/10 px-4 py-3 sm:px-5">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-[#000000]">
                      <FiCalendar size={16} className="text-[#000000] shrink-0" />
                      {formatDate(order.createdAt)}
                    </span>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${statusInfo.className}`}
                    >
                      {statusInfo.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {status === "pending" && (
                      <button
                        type="button"
                        disabled={isUpdating}
                        onClick={() => handleStatusChange(order.id, "confirmed")}
                        className="flex items-center gap-2 min-h-[40px] rounded-xl bg-[#e0d8c3] px-4 py-2 text-sm font-bold text-[#000000] hover:opacity-90 transition disabled:opacity-60"
                      >
                        <FiCheck size={18} />
                        {isUpdating ? "..." : "E konfirmuar"}
                      </button>
                    )}
                    {status === "confirmed" && (
                      <button
                        type="button"
                        disabled={isUpdating}
                        onClick={() => handleStatusChange(order.id, "perfunduar")}
                        className="flex items-center gap-2 min-h-[40px] rounded-xl bg-[#000000] text-white px-4 py-2 text-sm font-bold hover:opacity-90 transition disabled:opacity-60"
                      >
                        <FiPackage size={18} />
                        {isUpdating ? "..." : "Perfunduar"}
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={deletingId === order.id}
                      onClick={() => handleDeleteOrder(order.id)}
                      className="flex items-center gap-2 min-h-[40px] rounded-xl border-2 border-red-200 bg-white px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50 transition disabled:opacity-60"
                      title="Fshij porosinë"
                    >
                      <FiTrash2 size={18} />
                      {deletingId === order.id ? "..." : "Fshij"}
                    </button>
                  </div>
                </div>

                <div className="p-4 sm:p-5">
                  <div className="grid gap-6 lg:grid-cols-[1fr,auto]">
                    {/* Left: Customer + Products table */}
                    <div className="space-y-5 min-w-0">
                      {/* Customer block */}
                      <div>
                        <h3 className="text-xs font-bold uppercase tracking-wider text-[#a6a6a6] mb-3">
                          Klienti
                        </h3>
                        <div className="rounded-xl border border-[#a6a6a6]/30 bg-white p-4 space-y-2">
                          <p className="flex items-center gap-2 font-bold text-[#000000]">
                            <FiUser size={16} className="text-[#000000] shrink-0" />
                            {fullName}
                          </p>
                          <p className="flex items-center gap-2 text-sm text-[#a6a6a6]">
                            <FiPhone size={14} className="shrink-0" />
                            <a href={`tel:${c.phone || ""}`} className="hover:text-[#000000]">
                              {c.phone || "—"}
                            </a>
                          </p>
                          <p className="flex items-start gap-2 text-sm text-[#a6a6a6]">
                            <FiMapPin size={14} className="shrink-0 mt-0.5" />
                            <span>{c.address || "—"}</span>
                          </p>
                        </div>
                      </div>

                      {/* Products table */}
                      <div>
                        <h3 className="text-xs font-bold uppercase tracking-wider text-[#a6a6a6] mb-3">
                          Produktet
                        </h3>
                        <div className="rounded-xl border border-[#a6a6a6]/30 overflow-hidden">
                          {/* Table on larger screens */}
                          <div className="hidden sm:block overflow-x-auto">
                            <table className="w-full text-left">
                              <thead>
                                <tr className="bg-[#e0d8c3]/20 border-b border-[#a6a6a6]/30">
                                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#a6a6a6]">
                                    Produkti
                                  </th>
                                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#a6a6a6] w-20 text-center">
                                    Sasi
                                  </th>
                                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#a6a6a6] w-24 text-right">
                                    Çmimi
                                  </th>
                                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#a6a6a6] w-28 text-right">
                                    Në total
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {items.map((item, idx) => {
                                  const qty = item.quantity || 1;
                                  const lineTotal = (item.price || 0) * qty;
                                  return (
                                    <tr
                                      key={idx}
                                      className="border-b border-[#a6a6a6]/20 last:border-0"
                                    >
                                      <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                          <div className="h-12 w-12 rounded-lg bg-[#e0d8c3]/20 overflow-hidden shrink-0">
                                            {item.imageUrl ? (
                                              <img
                                                src={item.imageUrl}
                                                alt={item.name}
                                                className="h-full w-full object-cover"
                                              />
                                            ) : (
                                              <div className="h-full w-full flex items-center justify-center text-[#000000]/40 text-xs">
                                                —
                                              </div>
                                            )}
                                          </div>
                                          <span className="font-semibold text-[#000000]">
                                            {item.name}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 text-center font-semibold text-[#000000]">
                                        {qty}
                                      </td>
                                      <td className="px-4 py-3 text-right text-[#a6a6a6]">
                                        €{Number(item.price).toFixed(2)}
                                      </td>
                                      <td className="px-4 py-3 text-right font-bold text-[#000000]">
                                        €{lineTotal.toFixed(2)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          {/* Mobile: list with image + details */}
                          <div className="sm:hidden divide-y divide-[#a6a6a6]/30">
                            {items.map((item, idx) => {
                              const qty = item.quantity || 1;
                              const lineTotal = (item.price || 0) * qty;
                              return (
                                <div
                                  key={idx}
                                  className="flex gap-3 p-4"
                                >
                                  <div className="h-14 w-14 rounded-lg bg-[#e0d8c3]/20 overflow-hidden shrink-0">
                                    {item.imageUrl ? (
                                      <img
                                        src={item.imageUrl}
                                        alt={item.name}
                                        className="h-full w-full object-cover"
                                      />
                                    ) : (
                                      <div className="h-full w-full flex items-center justify-center text-[#000000]/40 text-xs">
                                        —
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-bold text-[#000000]">
                                      {item.name}
                                    </p>
                                    <p className="text-sm text-[#a6a6a6]">
                                      {qty} × €{Number(item.price).toFixed(2)} = €{lineTotal.toFixed(2)}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right: Total card */}
                    <div className="lg:w-56 shrink-0">
                      <div className="rounded-xl border-2 border-[#e0d8c3]/50 bg-[#e0d8c3]/5 p-5 text-center">
                        <p className="text-xs font-bold uppercase tracking-wider text-[#a6a6a6] mb-1">
                          Total porosie
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold text-[#000000]">
                          €{Number(order.total || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
